import { z } from 'zod'
import { matchConfigSchema } from './game.js'

/**
 * Fixed per-seat identity colours. A player's colour never changes mid-match,
 * so "who is winning" is readable without reading names. Index maps to a seat,
 * not to a user.
 */
export const PLAYER_COLOR_KEYS = ['lime', 'cyan', 'magenta', 'orange'] as const
export type PlayerColorKey = (typeof PLAYER_COLOR_KEYS)[number]

export const ROOM_CODE_LENGTH = 6
/** Ambiguous glyphs (0/O, 1/I/L) are excluded so codes survive being read aloud. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
export const roomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(ROOM_CODE_LENGTH)
  .regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]+$`), 'Invalid room code')

export const playerSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  seat: z.number().int().min(0).max(3),
  color: z.enum(PLAYER_COLOR_KEYS),
  isHost: z.boolean(),
  ready: z.boolean(),
  connected: z.boolean(),
  /** A practice opponent driven by the server, not a person with a socket. */
  isBot: z.boolean(),
  /** Set once the match starts; players eliminated in the counting modes. */
  eliminated: z.boolean(),
  /** Click Race running total, or rounds survived in the counting modes. */
  score: z.number().int().min(0),
})
export type Player = z.infer<typeof playerSchema>

export const ROOM_STATUSES = [
  'lobby',
  'countdown',
  'briefing',
  'displaying',
  'collecting',
  'round_result',
  'finished',
  'voided',
] as const
export const roomStatusSchema = z.enum(ROOM_STATUSES)
export type RoomStatus = z.infer<typeof roomStatusSchema>

export const roomSchema = z.object({
  id: z.string(),
  code: z.string(),
  hostId: z.string(),
  config: matchConfigSchema,
  players: z.array(playerSchema),
  status: roomStatusSchema,
  /** Null until the match starts. */
  matchId: z.string().nullable(),
  pot: z.number().int().min(0),
  roundIndex: z.number().int().min(0),
  isPublic: z.boolean(),
})
export type Room = z.infer<typeof roomSchema>

export const ROOM_ERROR_CODES = [
  'room_not_found',
  'room_full',
  'room_in_progress',
  'not_host',
  'insufficient_coins',
  'insufficient_prize_coins',
  'money_not_allowed',
  'age_not_verified',
  'already_in_room',
  'not_in_room',
  'invalid_config',
  'not_enough_players',
  'players_not_ready',
  'rate_limited',
  'unauthorized',
  'internal',
] as const
export const roomErrorCodeSchema = z.enum(ROOM_ERROR_CODES)
export type RoomErrorCode = z.infer<typeof roomErrorCodeSchema>

export const ROOM_ERROR_MESSAGES: Record<RoomErrorCode, string> = {
  room_not_found: 'No room with that code. Check the characters and try again.',
  room_full: 'That room is already full.',
  room_in_progress: 'That match has already started.',
  not_host: 'Only the host can change that.',
  insufficient_coins: 'You do not have enough practice coins for this stake.',
  insufficient_prize_coins:
    'You do not have enough prize coins for this stake. Convert some of your balance first.',
  money_not_allowed: 'Prize matches are not available where you are playing from.',
  age_not_verified: 'Confirm your date of birth before joining a prize match.',
  already_in_room: 'You are already in a room. Leave it first.',
  not_in_room: 'You are not in a room.',
  invalid_config: 'Those match settings are not valid.',
  not_enough_players: 'You need at least two players to start.',
  players_not_ready: 'Everyone has to be ready before the match starts.',
  rate_limited: 'Slow down for a moment, then try again.',
  unauthorized: 'You need to sign in to do that.',
  internal: 'Something went wrong on our side. Try again.',
}
